import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatQuantity } from '@/utils/formatters';
import { 
  Package, Search, RefreshCw, Loader2, DollarSign, MapPin, 
  ShoppingCart, Trash2, Calendar, FileText, Building2, AlertTriangle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const statusColors = {
  'In Use': 'bg-green-100 text-green-800 border-green-200',
  'Sold': 'bg-blue-100 text-blue-800 border-blue-200',
  'Disposed': 'bg-red-100 text-red-800 border-red-200'
};

export default function AssetRegisterPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  // Data state
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({ total: 0, in_use: 0, sold: 0, disposed: 0, total_value: 0 });
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Sell dialog state
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [sellForm, setSellForm] = useState({
    client_id: '',
    sale_price: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [selling, setSelling] = useState(false);
  
  // Dispose dialog state
  const [disposeDialogOpen, setDisposeDialogOpen] = useState(false);
  const [disposeForm, setDisposeForm] = useState({
    disposal_notes: '',
    disposal_date: new Date().toISOString().split('T')[0]
  });
  const [disposing, setDisposing] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [assetsRes, locationsRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/assets/register`, { 
          headers: getAuthHeader(),
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            location_id: locationFilter !== 'all' ? locationFilter : undefined
          }
        }),
        axios.get(`${API_URL}/locations`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/clients`, { headers: getAuthHeader() })
      ]);
      
      setAssets(assetsRes.data.assets || []);
      setSummary(assetsRes.data.summary || {});
      setLocations(locationsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load asset register');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, statusFilter, locationFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter assets by search term
  const filteredAssets = assets.filter(asset => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      asset.name?.toLowerCase().includes(search) ||
      asset.item_code?.toLowerCase().includes(search) ||
      asset.description?.toLowerCase().includes(search)
    );
  });

  // Open sell dialog
  const openSellDialog = (asset) => {
    setSelectedAsset(asset);
    setSellForm({
      client_id: '',
      sale_price: asset.asset_purchase_value || '',
      sale_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setSellDialogOpen(true);
  };

  // Handle sell asset
  const handleSellAsset = async () => {
    if (!sellForm.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!sellForm.sale_price || parseFloat(sellForm.sale_price) <= 0) {
      toast.error('Please enter a valid sale price');
      return;
    }

    try {
      setSelling(true);
      const response = await axios.post(`${API_URL}/assets/sell`, {
        item_id: selectedAsset.id,
        client_id: sellForm.client_id,
        sale_price: parseFloat(sellForm.sale_price),
        sale_date: sellForm.sale_date,
        notes: sellForm.notes
      }, { headers: getAuthHeader() });

      toast.success(response.data.message || 'Asset sale initiated');
      setSellDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error selling asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to sell asset');
    } finally {
      setSelling(false);
    }
  };

  // Open dispose dialog
  const openDisposeDialog = (asset) => {
    setSelectedAsset(asset);
    setDisposeForm({
      disposal_notes: '',
      disposal_date: new Date().toISOString().split('T')[0]
    });
    setDisposeDialogOpen(true);
  };

  // Handle dispose asset
  const handleDisposeAsset = async () => {
    if (!disposeForm.disposal_notes.trim()) {
      toast.error('Please provide disposal notes');
      return;
    }

    try {
      setDisposing(true);
      const response = await axios.post(`${API_URL}/assets/dispose`, {
        item_id: selectedAsset.id,
        disposal_notes: disposeForm.disposal_notes,
        disposal_date: disposeForm.disposal_date
      }, { headers: getAuthHeader() });

      toast.success(response.data.message || 'Asset disposed');
      setDisposeDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error disposing asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to dispose asset');
    } finally {
      setDisposing(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="asset-register-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-orange-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Asset Register</h1>
              <p className="text-sm text-slate-500">Track and manage company assets</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/production/items')}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="add-asset-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Add Asset
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Assets</p>
                <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Package className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-600">In Use</p>
                <p className="text-2xl font-bold text-green-700">{summary.in_use}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Sold</p>
                <p className="text-2xl font-bold text-blue-700">{summary.sold}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-600">Disposed</p>
                <p className="text-2xl font-bold text-red-700">{summary.disposed}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-600">Total Value (In Use)</p>
                <p className="text-2xl font-bold text-orange-700">₹{formatQuantity(summary.total_value)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label className="text-sm mb-2 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name or code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div className="w-48">
                <Label className="text-sm mb-2 block">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="In Use">In Use</SelectItem>
                    <SelectItem value="Sold">Sold</SelectItem>
                    <SelectItem value="Disposed">Disposed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label className="text-sm mb-2 block">Location</Label>
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger data-testid="location-filter">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assets Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Assets ({filteredAssets.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No assets found</p>
                <p className="text-sm mt-1">Create an item with category "ASSET" to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Purchase Date</TableHead>
                    <TableHead className="text-right">Purchase Value</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.map(asset => (
                    <TableRow key={asset.id} data-testid={`asset-row-${asset.id}`}>
                      <TableCell className="font-medium">{asset.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {asset.item_code || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          {formatDate(asset.asset_purchase_date || asset.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ₹{formatQuantity(asset.asset_purchase_value || 0)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3 text-slate-400" />
                          {asset.location_name || asset.default_location_name || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[asset.asset_status] || statusColors['In Use']}>
                          {asset.asset_status || 'In Use'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          {(!asset.asset_status || asset.asset_status === 'In Use') && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openSellDialog(asset)}
                                data-testid={`sell-btn-${asset.id}`}
                              >
                                <ShoppingCart className="h-3 w-3 mr-1" />
                                Sell
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openDisposeDialog(asset)}
                                data-testid={`dispose-btn-${asset.id}`}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Dispose
                              </Button>
                            </>
                          )}
                          {asset.asset_status === 'Sold' && (
                            <span className="text-sm text-slate-500">
                              Sold on {formatDate(asset.asset_sold_date)}
                            </span>
                          )}
                          {asset.asset_status === 'Disposed' && (
                            <span className="text-sm text-slate-500">
                              Disposed on {formatDate(asset.asset_disposed_date)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sell Dialog */}
        <Dialog open={sellDialogOpen} onOpenChange={setSellDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-600" />
                Sell Asset
              </DialogTitle>
              <DialogDescription>
                Create sale documents for {selectedAsset?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">{selectedAsset?.name}</p>
                <p className="text-xs text-slate-500">{selectedAsset?.item_code}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select 
                  value={sellForm.client_id} 
                  onValueChange={(v) => setSellForm({...sellForm, client_id: v})}
                >
                  <SelectTrigger data-testid="sell-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Sale Price *</Label>
                <Input
                  type="number"
                  value={sellForm.sale_price}
                  onChange={(e) => setSellForm({...sellForm, sale_price: e.target.value})}
                  placeholder="Enter sale price"
                  data-testid="sell-price-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Sale Date</Label>
                <Input
                  type="date"
                  value={sellForm.sale_date}
                  onChange={(e) => setSellForm({...sellForm, sale_date: e.target.value})}
                  data-testid="sell-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={sellForm.notes}
                  onChange={(e) => setSellForm({...sellForm, notes: e.target.value})}
                  placeholder="Optional notes"
                  rows={2}
                />
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">This will create:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>Invoice for the sale</li>
                  <li>Delivery Challan (DC)</li>
                  <li>Gate Pass for dispatch</li>
                </ul>
                <p className="text-xs mt-2">Asset status will change to "Sold" when Gate Pass is marked as out.</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setSellDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSellAsset} 
                disabled={selling}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {selling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
                Create Sale Documents
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dispose Dialog */}
        <Dialog open={disposeDialogOpen} onOpenChange={setDisposeDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Dispose Asset
              </DialogTitle>
              <DialogDescription>
                Mark {selectedAsset?.name} as disposed
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800">{selectedAsset?.name}</p>
                <p className="text-xs text-red-600">{selectedAsset?.item_code}</p>
                <p className="text-xs text-red-600 mt-1">
                  Purchase Value: ₹{formatQuantity(selectedAsset?.asset_purchase_value || 0)}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Disposal Date</Label>
                <Input
                  type="date"
                  value={disposeForm.disposal_date}
                  onChange={(e) => setDisposeForm({...disposeForm, disposal_date: e.target.value})}
                  data-testid="dispose-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Reason for Disposal *</Label>
                <Textarea
                  value={disposeForm.disposal_notes}
                  onChange={(e) => setDisposeForm({...disposeForm, disposal_notes: e.target.value})}
                  placeholder="Enter reason (e.g., damaged, obsolete, written off)"
                  rows={3}
                  data-testid="dispose-notes-input"
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <p className="font-medium">Warning</p>
                <p className="text-xs mt-1">This action will remove the asset from inventory. This cannot be undone.</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisposeDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDisposeAsset} 
                disabled={disposing}
                variant="destructive"
              >
                {disposing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Confirm Disposal
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
